/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useRef } from 'react';

import { Grid, PageGridContainer, typography2 } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';

import { CoachChat } from 'bundles/ai-coach-platform/components/patterns';
import { ChatEmptyState } from 'bundles/ai-coach-platform/components/patterns/chat/common';
import coachStore from 'bundles/ai-coach-platform/components/patterns/chat/store';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import type { CustomSendButtonProps } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { isCoachMessage } from 'bundles/ai-coach-platform/components/patterns/chat/utils/messageUtils';
import { usePageHeaderInfoState } from 'bundles/composable-page-header/hooks/usePageHeaderInfoState';
import CourseraAiFooter from 'bundles/unified-common/components/coach-on-discovery/CourseraAiFooter';
import CourseraAiHeader from 'bundles/unified-common/components/coach-on-discovery/CourseraAiHeader';
import CourseraAiMessage from 'bundles/unified-common/components/coach-on-discovery/CourseraAiMessage';
import CourseraAiMessageLoader from 'bundles/unified-common/components/coach-on-discovery/CourseraAiMessageLoader';
import CourseraAiTemporaryStreamedMessage from 'bundles/unified-common/components/coach-on-discovery/CourseraAiTemporaryStreamedMessage';
import FullScreenCoachChatContainer from 'bundles/unified-common/components/coach-on-discovery/FullScreenCoachChatContainer';
import { useGlobalCoachChatState } from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import { useNavAiSparkle } from 'bundles/unified-common/providers/NavAiSparkleExperimentProvider';
import FirstLearnerMessage from 'bundles/unified-description-page-common/components/coach/FirstLearnerMessage';
import TwoPanelChatSendButton from 'bundles/unified-description-page-common/components/conversational-discovery/TwoPanelChatSendButton';

import _t from 'i18n!nls/unified-common';

const styles = {
  containerAdditionalStyles: css`
    box-shadow: none;

    /* Suppress the platform's bottom border on the header container so the custom
     * TwoPanelChatHeader sits flush against the message list per the two-panel design. */
    .coach-conversation-container > div:first-child {
      border-bottom: none;
    }
  `,
  coachChatAdditionalStyles: css`
    padding: 0;

    .coach-message-client {
      display: flex;
      align-items: flex-end;
    }

    // Maintain consistent textfield background when send button is clicked.
    .cds-inputAdornment-filled {
      background: transparent;
    }

    // Remove visible border between input field and send button after sending a message.
    && .cds-inputAdornment-end {
      border-inline-start: 0;
    }
  `,
  chatHeaderContainer: css`
    padding-left: 0;
    padding-right: 0;
  `,
  recommendedActionButton: css`
    box-shadow: inset 0 0 0 1px var(--cds-color-neutral-stroke-primary-weak);
    color: var(--cds-color-neutral-primary);
    background: var(--cds-color-neutral-background-primary);
    font: ${typography2.bodySecondary.font};

    &::before {
      inset: 1px !important;
      border-radius: 7px !important;
    }
  `,
};

const renderCustomChatSendButton = (props: CustomSendButtonProps) => <TwoPanelChatSendButton {...props} />;

const RECOMMENDED_ACTIONS_CONTEXT_KEY = 'additionalContext.previous_pills';

const GlobalCoachChat = () => {
  const { showNavAiSparkle } = useNavAiSparkle();
  const { pageChatType, contextConfig, setContextConfig } = useGlobalCoachChatState();
  const { height, shouldOffsetHeader, scrollMovementOffset } = usePageHeaderInfoState();
  const topOffset = shouldOffsetHeader ? height - scrollMovementOffset : height;
  const prevLabelsRef = useRef<string | null>(null);

  useEffect(() => {
    const syncLabels = () => {
      const { messages } = (coachStore.getState() as GlobalState).messages;
      const labels = Array.from(
        new Set(
          messages
            .flatMap((msg) => msg.recommendedActions ?? [])
            .map((a) => a.actionLabel)
            .filter(Boolean)
        )
      );
      const labelsJson = labels.length > 0 ? JSON.stringify(labels) : null;
      if (prevLabelsRef.current === labelsJson) return;
      prevLabelsRef.current = labelsJson;
      setContextConfig((prev) => ({
        ...prev,
        additionalContext: [
          ...(prev.additionalContext?.filter((ctx) => ctx.contextKey !== RECOMMENDED_ACTIONS_CONTEXT_KEY) ?? []),
          ...(labelsJson ? [{ contextKey: RECOMMENDED_ACTIONS_CONTEXT_KEY, contextValue: labelsJson }] : []),
        ],
      }));
    };

    syncLabels();
    return coachStore.subscribe(syncLabels);
  }, [setContextConfig]);

  if (!showNavAiSparkle || pageChatType !== null) return null;

  return (
    <FullScreenCoachChatContainer topOffset={topOffset} additionalStyles={styles.containerAdditionalStyles}>
      <CoachChat
        additionalStyles={styles.coachChatAdditionalStyles}
        enableBrandUpdate={true}
        aria-label={_t('Ask Coursera chat')}
        inputLabel={_t('Ask Coursera')}
        inputPlaceholder={_t('Ask anything...')}
        chatMessageListAriaLabel={_t('Ask Coursera chat messages')}
        mode={contextConfig.config.coachModeId}
        contextConfig={contextConfig}
        customHeader={
          <PageGridContainer maxWidth="lg">
            <Grid item xs={12} css={styles.chatHeaderContainer}>
              <CourseraAiHeader
                icon={<AIGenerateBrandedNewIcon size="large" />}
                hideTitle={true}
                closeBtnEventLocation="chat"
              />
            </Grid>
          </PageGridContainer>
        }
        renderCustomMessage={(props) => {
          const firstLearnerMessageIndex = props.messages.findIndex((m) => !isCoachMessage(m.sender));
          const isFirstLearnerMessage = firstLearnerMessageIndex !== -1 && firstLearnerMessageIndex === props.index;

          if (isFirstLearnerMessage && props.messages.length < 5) {
            return <FirstLearnerMessage {...props} recommendedActionsLabel="" />;
          }

          return <CourseraAiMessage {...props} recommendedActionButtonCustomStyles={styles.recommendedActionButton} />;
        }}
        renderCustomMessageLoader={(props) => <CourseraAiMessageLoader {...props} />}
        renderCustomTemporaryStreamedMessage={(props) => <CourseraAiTemporaryStreamedMessage {...props} />}
        customFooter={<CourseraAiFooter />}
        emptyState={<ChatEmptyState title={_t('Hi there!')} subtitle={_t('How can I help?')} />}
        enableTextStreaming={true}
        enableLoggedOutMode={true}
        storeSessionId={true}
        showSettings={false}
        fetchChatHistoryOnInflightSend={false}
        renderCustomSendButton={renderCustomChatSendButton}
        fetchChatHistoryOnLocationChange={false}
      />
    </FullScreenCoachChatContainer>
  );
};

export default GlobalCoachChat;
